import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { checkCacheForPropertyValue } from "../helpers/CacheManagement";
import Header from "../components/LayoutComponents/Header";
import Footer from "../components/LayoutComponents/Footer";
import ScrollToTop from "../components/LayoutComponents/ScrollToTop";
import RouteMetaTags from "../components/Utilities/RouteMetaTags";
import UpdateNotice from "../components/Utilities/UpdateNotice";
import Spinner from "../components/Utilities/Spinner";

// ----- Pages (Lazy loaded for code splitting)
const About = React.lazy(() => import("../pages/About"));
const Donate = React.lazy(() => import("../pages/Donate"));
const Caller = React.lazy(() => import("../pages/Caller"));
const Contact = React.lazy(() => import("../pages/Contact"));
const FAQ = React.lazy(() => import("../pages/FAQ"));
const Generator = React.lazy(() => import("../pages/Generator"));
const Guide = React.lazy(() => import("../pages/Guide"));
const Layouts = React.lazy(() => import("../pages/Layouts"));
const Help = React.lazy(() => import("../pages/Help"));
const NotFound = React.lazy(() => import("../pages/NotFound"));
const Patterns = React.lazy(() => import("../pages/Patterns"));
const Play = React.lazy(() => import("../pages/Play"));
const Privacy = React.lazy(() => import("../pages/Privacy"));
const ReleaseNotes = React.lazy(() => import("../pages/ReleaseNotes"));
const ResetPage = React.lazy(() => import("../pages/Reset"));
const SubmitAPattern = React.lazy(() => import("../pages/SubmitAPattern"));
const Terms = React.lazy(() => import("../pages/Terms"));
const Thanks = React.lazy(() => import("../pages/Thanks"));
const Transparency = React.lazy(() => import("../pages/Transparency"));

// Number of errors in a short window before we suggest resetting settings.
const REPEATED_ERROR_THRESHOLD = 3;
// Window (ms) after which the repeated-error count starts over.
const REPEATED_ERROR_WINDOW = 10 * 60 * 1000;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, repeatedErrorCount: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidMount() {
    // A successful mount means the app loaded fine, so a future chunk failure this
    // session should get its own reload attempt rather than being blocked by a stale flag.
    sessionStorage.removeItem("lpb-chunk-reload-attempted");
  }

  componentDidCatch(error) {
    const isChunkLoadError = error?.name === "ChunkLoadError" || /Loading chunk .* failed/.test(error?.message || "") || /Failed to fetch dynamically imported module/.test(error?.message || "");

    // A failed lazy-loaded chunk (eg. a transient network blip) is often fixed by a single
    // reload once the CDN/service worker has a fresh copy — don't send the user straight to
    // the destructive reset page for what may just be a hiccup.
    if (isChunkLoadError && !sessionStorage.getItem("lpb-chunk-reload-attempted")) {
      sessionStorage.setItem("lpb-chunk-reload-attempted", "true");
      window.location.reload();
      return;
    }

    const now = Date.now();
    const path = window.location.pathname;

    // Record the latest error so the Contact page can prefill a bug report if the
    // user chooses to reach out.
    this.safeSessionWrite("lpb-last-error", JSON.stringify({ message: error?.message || String(error), path: path, time: new Date(now).toISOString() }));

    // Track how many errors have happened in a short window. Only escalate the
    // messaging after several repeats — not on the first, recoverable error.
    let previous = {};
    try {
      previous = JSON.parse(sessionStorage.getItem("lpb-error-tracker")) || {};
    } catch {
      previous = {};
    }
    const withinWindow = previous.lastErrorTime && now - previous.lastErrorTime < REPEATED_ERROR_WINDOW;
    const repeatedErrorCount = (withinWindow ? previous.count || 0 : 0) + 1;
    this.safeSessionWrite("lpb-error-tracker", JSON.stringify({ count: repeatedErrorCount, lastErrorTime: now }));

    this.setState({ repeatedErrorCount: repeatedErrorCount });
  }

  safeSessionWrite(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Session storage may be unavailable (private mode, quota) — non-fatal.
    }
  }

  handleTryAgain = () => {
    this.setState({ hasError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const showResetSuggestion = this.state.repeatedErrorCount >= REPEATED_ERROR_THRESHOLD;

    return (
      <section className="error-boundary-fallback">
        <div className="row container justify-start">
          <div className="col full-width padding-vertical-xlg">
            <h1>Something went wrong on this page</h1>
            <p>Sorry about that. The rest of the app is still working — you can try this page again, reload, or head back to the caller.</p>

            <div className="margin-vertical-lg">
              <button className="primary-button margin-right-sm" onClick={this.handleTryAgain}>
                Try Again
              </button>
              <button className="secondary-button margin-right-sm" onClick={this.handleReload}>
                Reload Page
              </button>
              <a className="secondary-button" href="/">
                Go to Home
              </a>
            </div>

            {showResetSuggestion && (
              <p>
                This keeps happening. If it continues, <a href="/reset">resetting your settings</a> may help, or you can <a href="/contact">contact us</a> for support.
              </p>
            )}
            {!showResetSuggestion && (
              <p className="small-text">
                Still stuck? You can <a href="/contact">contact us</a> or, as a last resort, <a href="/reset">reset your settings</a>.
              </p>
            )}
          </div>
        </div>
      </section>
    );
  }
}

// Keying the boundary on the current path means navigating to a different page
// always clears a stuck error state instead of re-showing the fallback forever.
function RoutedErrorBoundary(props) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{props.children}</ErrorBoundary>;
}

class AppRoutes extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      colorScheme: "dark",
      theme: "lpb",
      hideFooter: false,
      layout: "classic",
    };

    const cachedSettings = checkCacheForPropertyValue("settings");
    if (cachedSettings !== null) {
      if (Object.prototype.hasOwnProperty.call(cachedSettings, "theme")) {
        this.state.theme = cachedSettings.theme;
      }
      if (Object.prototype.hasOwnProperty.call(cachedSettings, "hideFooter")) {
        this.state.hideFooter = cachedSettings.hideFooter;
      }
      if (Object.prototype.hasOwnProperty.call(cachedSettings, "layout")) {
        this.state.layout = cachedSettings.layout;
      }
    }

    const colorScheme = localStorage.getItem("lpb-colorscheme");
    if (colorScheme) {
      this.state.colorScheme = colorScheme;
    }
    document.addEventListener("settingssaved", this.handleSettingsSaved);
    document.addEventListener("resetsettings", this.handleSettingsSaved);
    document.addEventListener("togglecolorscheme", this.toggleColorScheme);
  }

  componentDidMount() {}

  componentWillUnmount() {
    document.removeEventListener("settingssaved", this.handleSettingsSaved);
    document.removeEventListener("resetsettings", this.handleSettingsSaved);
    document.removeEventListener("togglecolorscheme", this.toggleColorScheme);
  }

  toggleColorScheme = (event) => {
    localStorage.setItem("lpb-colorscheme", event.detail);
    this.setState({ colorScheme: event.detail });
  };

  handleSettingsSaved = (event) => {
    const newSettings = event.detail;
    this.setState({
      cardDisplayColor: newSettings.cardDisplayColor,
      theme: newSettings.theme,
      hideFooter: newSettings.hideFooter,
      layout: newSettings.layout,
    });
  };

  render() {
    return (
      <div className={`body-container ${this.state.theme} ${this.state.colorScheme}-scheme`}>
        <BrowserRouter>
          <Header colorScheme={this.state.colorScheme} layout={this.state.layout} />
          <ScrollToTop />
          <RouteMetaTags />
          <React.Suspense fallback={<Spinner variant="counter" />}>
            <RoutedErrorBoundary>
              <Routes>
                <Route path="/about" element={<About />} />
                <Route path="/donate" element={<Donate />} />
                <Route exact path="/" element={<Caller />} />
                <Route path="/generator" element={<Generator />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/help" element={<Help />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/layouts" element={<Layouts />} />
                <Route path="/patterns" element={<Patterns />} />
                <Route path="/submitpattern" element={<SubmitAPattern />} />
                <Route path="/play" element={<Play />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/releases" element={<ReleaseNotes />} />
                <Route path="/reset" element={<ResetPage />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/thanks" element={<Thanks />} />
                <Route path="/transparency" element={<Transparency />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </RoutedErrorBoundary>
          </React.Suspense>
          <Footer hideFooter={this.state.hideFooter} />
          <UpdateNotice />
        </BrowserRouter>
      </div>
    );
  }
}

export default AppRoutes;
